import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import {
  isActiveFreeTrial,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';

type ActivateEarlyTrialModalComponent =
  typeof import('./ActivateEarlyTrialModal').default;

let activateEarlyTrialModalImport:
  | Promise<{ default: ActivateEarlyTrialModalComponent }>
  | undefined;

function importActivateEarlyTrialModal() {
  activateEarlyTrialModalImport ??= import('./ActivateEarlyTrialModal').catch(
    (error) => {
      activateEarlyTrialModalImport = undefined;
      throw error;
    },
  );
  return activateEarlyTrialModalImport;
}

/**
 * Mount once per app (e.g. next to `PurchaseCreditsModalHost`) so
 * `openActivateEarlyTrialModal` can open the UI from anywhere.
 */
export default function ActivateEarlyTrialModalHost() {
  const snap = useSnapshot(updatedProductStore.activateEarlyTrialModalState);
  const { organizationInfo } = useSnapshot(userStore.state);
  const inOrgCreditsTrial =
    isOrgCreditsWorkspace(organizationInfo) &&
    isActiveFreeTrial(organizationInfo);
  const [ActivateEarlyTrialModal, setActivateEarlyTrialModal] =
    useState<ActivateEarlyTrialModalComponent | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!inOrgCreditsTrial) {
      updatedProductStore.closeActivateEarlyTrialModal();
    }
  }, [inOrgCreditsTrial]);

  useEffect(() => {
    if (!snap.isOpen || !inOrgCreditsTrial || ActivateEarlyTrialModal) return;
    void loadModal();
  }, [snap.isOpen, inOrgCreditsTrial, ActivateEarlyTrialModal]);

  function loadModal() {
    setLoadError(false);
    return importActivateEarlyTrialModal()
      .then(({ default: ModalComponent }) => {
        setActivateEarlyTrialModal(() => ModalComponent);
      })
      .catch(() => {
        setLoadError(true);
      });
  }

  if (!snap.isOpen || !inOrgCreditsTrial) {
    return null;
  }
  if (!ActivateEarlyTrialModal) {
    return (
      <div
        className="bg-primary border-primary fixed bottom-4 left-4 z-[70] flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-lg border p-3 shadow-menu"
        role={loadError ? 'alert' : 'status'}
        aria-live="polite"
      >
        <span className="text-primary">
          {loadError
            ? 'Unable to load trial activation.'
            : 'Loading trial activation…'}
        </span>
        {loadError && (
          <>
            <button
              type="button"
              className="text-primary hover:text-secondary rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => void loadModal()}
            >
              Retry
            </button>
            <button
              type="button"
              className="text-primary hover:text-secondary rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={updatedProductStore.closeActivateEarlyTrialModal}
            >
              Close
            </button>
          </>
        )}
        {!loadError && (
          <button
            type="button"
            className="text-primary hover:text-secondary rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={updatedProductStore.closeActivateEarlyTrialModal}
          >
            Cancel
          </button>
        )}
      </div>
    );
  }
  return (
    <ActivateEarlyTrialModal
      close={updatedProductStore.closeActivateEarlyTrialModal}
    />
  );
}
